import type { AsrBody } from "@/schemas/asr.schema";
import type { RequestConfig } from "@/services/api";
import { api } from "@/services/api";

interface CostDetails {
  value: number;
  currency: string;
}

export interface Usage {
  costs: number;
  cost_details: CostDetails;
}

export interface SentimentAnalysis {
  label: string;
  score: number;
  fragment: string;
  timestamp?: string;
}

export interface TopicDetection {
  label: string;
  confidence: number;
  fragment: string;
  timestamp?: string;
}

export interface DiarizationSegment {
  speaker: string;
  initial_time: number;
  end_time: number;
  text: string;
}

export interface LlmBoostDiffSegment {
  value: string;
  type: "unchanged" | "added" | "removed";
}

export interface LlmBoostUnitChange {
  i: number;
  original: string;
  boosted: string;
  diff: LlmBoostDiffSegment[];
}

export interface LlmBoostContent {
  text: string;
  segments?: DiarizationSegment[] | { text: string }[];
  changes?: LlmBoostUnitChange[];
}

export interface ASRJSONResponse {
  text: string;
  usage?: Usage;
  summarization?: string;
  sentiment_analysis?: SentimentAnalysis[];
  topic_detection?: TopicDetection[];
  custom?: Record<string, string | number | boolean>;
  llm_boost?: LlmBoostContent;
}

export interface DiarizationResponse extends ASRJSONResponse {
  segments: DiarizationSegment[];
}

export type ASRResponse = ASRJSONResponse | DiarizationResponse | string;

export const asrApi = {
  async transcribe(body: AsrBody, config?: RequestConfig): Promise<ASRResponse> {
    const formData = new FormData();
    formData.append("file", body.file);
    formData.append("model", body.model!);

    if (body.language) formData.append("language", body.language);
    if (body.prompt) formData.append("prompt", body.prompt);
    if (body.response_format) formData.append("response_format", body.response_format);
    if (body.insights) formData.append("insights", JSON.stringify(body.insights));
    if (body.anonymize) formData.append("anonymize", JSON.stringify(body.anonymize));
    if (body.llm_boost) formData.append("llm_boost", "true");
    if (body.audio_enhancer) {
      formData.append("audio_enhancer", body.audio_enhancer);
    }
    if (body.vad_preset) {
      formData.append("vad_preset", body.vad_preset);
    }

    const { data } = await api.post("/asr/transcribe", formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
    });

    return data;
  },
};
